import React, { useEffect, useState } from 'react';
import api from '@/utils/api';
import { labelForCapability } from './agentCapabilityLabels';
import './AgentCapabilityChips.scss';

interface AgentCapabilityPickerProps {
  /** Currently-selected capability keys. */
  value: string[];
  /** Called with the new full selection whenever a chip toggles. */
  onChange: (next: string[]) => void;
  /** Restrict to single-selection mode (used by Discover filter). Default false = multi-select. */
  single?: boolean;
  /** Grey out + disable interactions. */
  disabled?: boolean;
  /** Optional label above the chip row. */
  label?: string;
  /** Optional help text under the chip row. */
  hint?: string;
  className?: string;
}

/**
 * Interactive multi-select for the agent capability taxonomy.
 *
 * Fetches the current taxonomy from GET /api/agents/capabilities on
 * mount so adding a new bucket server-side surfaces in the picker
 * without a client redeploy. Falls back to whatever the caller
 * pre-populated `value` with if the fetch fails — the register form
 * still works, the user just won't see chips for undiscovered keys.
 *
 * Two use cases:
 *   - Register form: multi-select (owner picks any of the buckets)
 *   - Discover filter: `single` mode (one active bucket at a time,
 *     click again to clear)
 */
const AgentCapabilityPicker: React.FC<AgentCapabilityPickerProps> = ({
  value,
  onChange,
  single = false,
  disabled = false,
  label,
  hint,
  className = '',
}) => {
  const [taxonomy, setTaxonomy] = useState<string[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ capabilities: string[] }>('/agents/capabilities')
      .then((res) => {
        if (!cancelled) setTaxonomy(res.data.capabilities);
      })
      .catch(() => {
        // Silent — see component docstring for rationale.
        if (!cancelled) setTaxonomy([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggle = (key: string) => {
    if (disabled) return;
    if (single) {
      // Click the active chip to clear; click a different chip to swap.
      onChange(value[0] === key ? [] : [key]);
      return;
    }
    if (value.includes(key)) {
      onChange(value.filter((k) => k !== key));
    } else {
      onChange([...value, key]);
    }
  };

  // Render whatever the taxonomy loader gives us; fall back to the
  // caller-supplied `value` set on failure so at least the selected
  // chips are still visible + clearable.
  const keys = taxonomy && taxonomy.length > 0 ? taxonomy : value;

  return (
    <div className={`agent-capability-picker ${className}`.trim()}>
      {label && <span className="agent-capability-picker__label">{label}</span>}
      <ul className="agent-capability-chips agent-capability-chips--md agent-capability-chips--interactive">
        {keys.map((key) => {
          const active = value.includes(key);
          return (
            <li key={key}>
              <button
                type="button"
                className={`agent-capability-chip agent-capability-chip--interactive${
                  active ? ' is-active' : ''
                }`}
                onClick={() => handleToggle(key)}
                disabled={disabled}
                aria-pressed={active}
              >
                {labelForCapability(key)}
              </button>
            </li>
          );
        })}
      </ul>
      {hint && <p className="agent-capability-picker__hint">{hint}</p>}
    </div>
  );
};

export default AgentCapabilityPicker;
